import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useProject } from '../../utils/ProjectContext';
import DraggableTopPlan from './DraggableTopPlan';
import ImageSelectionModal from '../../modals/ImageSelectionModal';
import { getImage, setImage } from '../../utils/idbHelpers';
import DraggableThisThenItem from './DraggableThisThenItem';
import Spinner from "../../utils/Spinner";
import BottomNavBar from "../navigation/BottomNavBar";

// Material UI imports
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  Grid, 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  Divider,
  IconButton,
  InputLabel,
  Tooltip,
  useTheme,
  ThemeProvider,
  createTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

// Create a theme using the project's colors that will work with the global theme
const customTheme = createTheme({
  palette: {
    primary: {
      main: '#689D4A',
      light: '#B9D38B',
      dark: '#689D4A',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#B9D38B',
      light: '#EFF9D6',
      dark: '#B9D38B',
    },
    background: {
      default: '#A9DEE8',
      paper: '#62C4CF',
    },
    error: {
      main: '#B22222',
    },
    text: {
      primary: '#2E2E2E',
    },
    success: {
      main: '#4FAE45',
    }
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderColor: '#DDD',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
  },
});



// Styled components
const StyledImageItem = styled(Box)(({ theme }) => ({
  position: 'relative',
  margin: theme.spacing(1),
  '&:hover .delete-button': {
    opacity: 1,
  }
}));

const StyledImage = styled('img')({
  width: '180px',
  height: '180px',
  objectFit: 'cover',
  borderRadius: '8px',
  border: '1px solid var(--border-color)',
});

const DeleteButton = styled(IconButton)({
  position: 'absolute',
  top: '8px',
  right: '8px',
  backgroundColor: 'var(--danger-color)',
  color: 'white',
  opacity: 0,
  transition: 'opacity 0.2s ease',
  '&:hover': {
    backgroundColor: 'var(--danger-color)',
    filter: 'brightness(0.9)',
  },
  padding: '4px',
});

const SectionContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--background-color)',
  border: '1px solid var(--border-color)',
}));

const ThisThenEditor = () => {
    const {BASE_URL, projectName} = useProject();
    const { projectId } = useParams();
    const { fetchProjectDetails, projectData, setProjectData } = useProject();
    const [thisThenList, setThisThenList] = useState([]);
    const [selectedThisThenId, setSelectedThisThenId] = useState(null);
    const [currentThisThen, setCurrentThisThen] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isThis, setIsThis] = useState(true); // To keep track of which part (this/then) is being edited
    const token = localStorage.getItem('token');
    const [thisThenName, setThisThenName] = useState();
    const MAX_IMAGES_PER_SECTION = 2;

    useEffect(() => {
        const fetchProjectData = async () => {
            try {
                const data = await fetchProjectDetails(projectId);
                setProjectData(data);
                setThisThenList(data.project.data.thisThen || []);
                setIsLoading(false);

                // Select the first item in the list
                if (data.project.data.thisThen && data.project.data.thisThen.length > 0) {
                    const firstThisThenId = data.project.data.thisThen[0]._id;
                    setSelectedThisThenId(firstThisThenId);
                    const selectedThisThen = data.project.data.thisThen.find(item => item._id === firstThisThenId);
                    const thisImages = await fetchImagesForPart(selectedThisThen.this.images);
                    const thenImages = await fetchImagesForPart(selectedThisThen.then.images);
                    setCurrentThisThen({
                        ...selectedThisThen,
                        this: { ...selectedThisThen.this, images: thisImages },
                        then: { ...selectedThisThen.then, images: thenImages },
                    });
                }
            } catch (error) {
                console.error('Error fetching project data:', error);
            }
        };

        fetchProjectData();
    }, []);

    useEffect(() => {
        if (selectedThisThenId) {
            const selectedThisThen = thisThenList.find((item) => item._id === selectedThisThenId);
            if (selectedThisThen) {
                const fetchImages = async () => {
                    const thisImages = await fetchImagesForPart(selectedThisThen.this.images);
                    const thenImages = await fetchImagesForPart(selectedThisThen.then.images);
                    setCurrentThisThen({
                        ...selectedThisThen,
                        this: { ...selectedThisThen.this, images: thisImages },
                        then: { ...selectedThisThen.then, images: thenImages },
                    });
                };
                fetchImages();
            }
        }
    }, [selectedThisThenId, thisThenList]);

    const fetchImagesForPart = async (images) => {
        console.log('FETCH FOR PART', images);
        return await Promise.all(
            images.map(async (imageObj) => {
                // Get the image ID from either string format or object format
                const imageId = typeof imageObj === 'string' ? imageObj : imageObj.id;
                const imageDetails = projectData.project.data.images.find(image => image._id === imageId);

                if (imageDetails) {
                    let cachedImage = await getImage(projectId, imageDetails.name);
                    if (cachedImage) {
                        return {
                            ...imageDetails,
                            imageUrl: cachedImage,
                            id: imageId,
                            altName: typeof imageObj === 'object' ? imageObj.altName : imageDetails.name // Preserve existing altName
                        };
                    } else {
                        try {
                            const response = await fetch(`${BASE_URL}/project/${projectData.project._id}/image/${imageDetails.name}`, {
                                headers: {
                                    'Authorization': `Bearer ${token}`,
                                },
                            });
                            const data = await response.json();
                            await setImage(projectId, imageDetails.name, data.imageUrl);
                            return {
                                ...imageDetails,
                                imageUrl: data.imageUrl,
                                id: imageId,
                                altName: typeof imageObj === 'object' ? imageObj.altName : imageDetails.name
                            };
                        } catch (error) {
                            console.error('Error fetching image:', error);
                            return null;
                        }
                    }
                }
                return null;
            })
        );
    };


    const handleThisThenSelect = (thisThenId) => {
        setSelectedThisThenId(thisThenId);
    };

    const moveThisThenItem = (fromIndex, toIndex) => {
        const updatedList = [...thisThenList];
        const [movedItem] = updatedList.splice(fromIndex, 1);
        updatedList.splice(toIndex, 0, movedItem);
        setThisThenList(updatedList);
    };

    const moveImage = (fromIndex, toIndex, fromThisPart, toThisPart) => {
        const fromImages = fromThisPart ? [...currentThisThen.this.images] : [...currentThisThen.then.images];
        const toImages = toThisPart ? [...currentThisThen.this.images] : [...currentThisThen.then.images];

        const [movedImage] = fromImages.splice(fromIndex, 1);
        toImages.splice(toIndex, 0, movedImage);

        const updatedThisThen = {
            ...currentThisThen,
            this: fromThisPart ? { images: fromImages } : toThisPart ? { images: toImages } : currentThisThen.this,
            then: fromThisPart ? currentThisThen.then : toThisPart ? currentThisThen.then : { images: toImages }
        };

        setCurrentThisThen(updatedThisThen);
        setThisThenList(thisThenList.map(item => item._id === currentThisThen._id ? updatedThisThen : item));
    };



    const handleDeleteImage = (index, isThisPart) => {
        if (window.confirm("Are you sure you want to delete this item?")) {
            const updatedImages = isThisPart ? [...currentThisThen.this.images] : [...currentThisThen.then.images];
            updatedImages.splice(index, 1);

            const updatedThisThen = {
                ...currentThisThen,
                this: isThisPart ? { images: updatedImages } : currentThisThen.this,
                then: isThisPart ? currentThisThen.then : { images: updatedImages }
            };

            setCurrentThisThen(updatedThisThen);
            const updatedThisThenList = thisThenList.map(item => item._id === currentThisThen._id ? updatedThisThen : item);
            setThisThenList(updatedThisThenList);

            const updatedProjectData = {
                ...projectData,
                project: {
                    ...projectData.project,
                    data: {
                        ...projectData.project.data,
                        thisThen: updatedThisThenList
                    }
                }
            };

            setProjectData(updatedProjectData);
        }

    };

    const handleDeleteThisThen = async (thisThenId) => {
        const updatedThisThenList = thisThenList.filter(item => item._id !== thisThenId);
        setThisThenList(updatedThisThenList);
        const updatedProjectData = {
            ...projectData,
            project: {
                ...projectData.project,
                data: {
                    ...projectData.project.data,
                    thisThen: updatedThisThenList
                }
            }
        };
        setProjectData(updatedProjectData);

        await setProjectData(updatedProjectData);
        if (thisThenId === selectedThisThenId) {
            setSelectedThisThenId(updatedThisThenList.length > 0 ? updatedThisThenList[0]._id : null);
            setCurrentThisThen(updatedThisThenList.length > 0 ? updatedThisThenList[0] : null);
        }
    };

    const handleImageNameChange = async (imageId, newName, isThisPart) => {
        const section = isThisPart ? 'this' : 'then';
        const currentImages = currentThisThen[section].images;

        // Update the image altName in the current images array
        const updatedImages = currentImages.map(img => {
            if (img.id === imageId) {
                return { ...img, altName: newName }; // Change name to altName
            }
            return img;
        });

        // Create updated ThisThen object
        const updatedThisThen = {
            ...currentThisThen,
            [section]: {
                ...currentThisThen[section],
                images: updatedImages
            }
        };

        // Update the current ThisThen
        setCurrentThisThen(updatedThisThen);

        // Update the ThisThen in the list
        const updatedThisThenList = thisThenList.map(item =>
            item._id === currentThisThen._id ? updatedThisThen : item
        );

        // Update state and project data
        setThisThenList(updatedThisThenList);
        const updatedProjectData = {
            ...projectData,
            project: {
                ...projectData.project,
                data: {
                    ...projectData.project.data,
                    thisThen: updatedThisThenList
                }
            }
        };
        setProjectData(updatedProjectData);
    };

    const handleImageClick = (imageUrl) => {
        // Handle image click (e.g., show in modal)
    };

    const openModal = (isThisPart) => {
        setIsThis(isThisPart);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const fetchUpdatedProjectData = async () => {
        try {
            const data = await fetchProjectDetails(projectId);
            setProjectData(data);
            setThisThenList(data.project.data.thisThen || []);
        } catch (error) {
            console.error('Error fetching updated project data:', error);
        }
    };

    const handleSelectImage = async (imageId) => {
        const data = await fetchProjectDetails(projectId);
        setProjectData(data);
        setThisThenList(data.project.data.thisThen || []);

        const foundImage = data.project.data.images.find(image => image._id === imageId);

        if (foundImage) {
            // Create new image object with the required format
            const newImageObj = {
                id: imageId,
                name: foundImage.name,
                altName: foundImage.name // Set initial altName to the image name
            };

            const updatedImages = isThis
                ? [...currentThisThen.this.images, newImageObj]
                : [...currentThisThen.then.images, newImageObj];

            const updatedThisThen = {
                ...currentThisThen,
                this: isThis ? { images: updatedImages } : currentThisThen.this,
                then: isThis ? currentThisThen.then : { images: updatedImages }
            };

            setCurrentThisThen(updatedThisThen);
            const updatedThisThenList = thisThenList.map(item =>
                item._id === currentThisThen._id ? updatedThisThen : item
            );
            setThisThenList(updatedThisThenList);

            const updatedProjectData = {
                ...data,
                project: {
                    ...data.project,
                    data: {
                        ...data.project.data,
                        thisThen: updatedThisThenList
                    }
                }
            };

            setProjectData(updatedProjectData);
            closeModal();
        }
    };

    const handleCreateNewThisThen = async () => {
        const name = prompt('Enter the name for the new ThisThen:');
        if (name) {
            const response = await fetch(`${BASE_URL}/generate-objectid`);
            const data = await response.json();
            let newId = data.objectId;

            const newThisThen = {
                _id: newId,
                name: name,
                this: { images: [] },
                then: { images: [] }
            };

            const updatedThisThenList = [...thisThenList, newThisThen];
            setThisThenList(updatedThisThenList);
            const updatedProjectData = {
                ...projectData,
                project: {
                    ...projectData.project,
                    data: {
                        ...projectData.project.data,
                        thisThen: updatedThisThenList
                    }
                }
            };
            setProjectData(updatedProjectData);
            setSelectedThisThenId(newThisThen._id);
            setCurrentThisThen(newThisThen);
        }
    };

    const handleImageAltNameChange = (imageId, newAltName, isThisPart) => {
        const updatedImages = isThisPart
            ? currentThisThen.this.images.map(img =>
                img.id === imageId ? { ...img, altName: newAltName } : img
            )
            : currentThisThen.then.images.map(img =>
                img.id === imageId ? { ...img, altName: newAltName } : img
            );

        const updatedThisThen = {
            ...currentThisThen,
            this: isThisPart ? { images: updatedImages } : currentThisThen.this,
            then: isThisPart ? currentThisThen.then : { images: updatedImages }
        };

        setCurrentThisThen(updatedThisThen);
        const updatedThisThenList = thisThenList.map(item =>
            item._id === currentThisThen._id ? updatedThisThen : item
        );
        setThisThenList(updatedThisThenList);

        const updatedProjectData = {
            ...projectData,
            project: {
                ...projectData.project,
                data: {
                    ...projectData.project.data,
                    thisThen: updatedThisThenList
                }
            }
        };
        setProjectData(updatedProjectData);
    };

    const handleNameChange = (e) => {
        setThisThenName(e.target.value);

        // Update the current ThisThen with the new name
        const updatedCurrentThisThen = { ...currentThisThen, name: e.target.value };
        setCurrentThisThen(updatedCurrentThisThen);

        // Update the ThisThen list
        const updatedThisThenList = thisThenList.map(thisThen =>
            thisThen._id === currentThisThen._id ? updatedCurrentThisThen : thisThen
        );
        setThisThenList(updatedThisThenList);

        // Update the project data
        const updatedProjectData = {
            ...projectData,
            project: {
                ...projectData.project,
                data: {
                    ...projectData.project.data,
                    thisThen: updatedThisThenList
                }
            }
        };
        setProjectData(updatedProjectData);
    };


    const theme = useTheme();
    
    if (isLoading) {
        return (
            <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                minHeight="100vh"
            >
                <Spinner />
            </Box>
        );
    }
    
    return (
        <ThemeProvider theme={customTheme}>
        <Box sx={{ 
            bgcolor: 'var(--light-background)', 
            height: '100vh', 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <Box 
                sx={{ 
                    bgcolor: 'var(--primary-color)', 
                    color: 'white',
                    py: 1,
                    px: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Typography 
                    variant="h5" 
                    component="h1"
                    sx={{ fontWeight: 'medium' }}
                >
                    {projectName}'s First/Then Sequences
                </Typography>
                
                {currentThisThen && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="subtitle1" sx={{ color: 'white' }}>
                            Editing:
                        </Typography>
                        <TextField
                            size="small"
                            value={currentThisThen.name}
                            onChange={handleNameChange}
                            sx={{ 
                                width: 200,
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: 'white',
                                    borderRadius: 1
                                }
                            }}
                        />
                    </Box>
                )}
            </Box>
            
            <DndProvider backend={HTML5Backend}>
                <Box sx={{ 
                    display: 'flex', 
                    height: 'calc(100vh - 112px)', // Subtract header and bottom nav
                    overflow: 'hidden'
                }}>
                    {/* Left Sidebar - Saved Sequences */}
                    <Paper 
                        elevation={2}
                        sx={{ 
                            width: 200, 
                            p: 1.5, 
                            borderRadius: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            borderRight: '1px solid var(--border-color)'
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                SAVED SEQUENCES
                            </Typography>
                            <Tooltip title="Create New Sequence">
                                <IconButton 
                                    size="small" 
                                    color="primary" 
                                    onClick={handleCreateNewThisThen}
                                    sx={{ bgcolor: 'var(--light-background)' }}
                                >
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        
                        <Box sx={{ 
                            overflowY: 'auto', 
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                        }}>
                            {thisThenList.map((item, index) => (
                                <DraggableThisThenItem
                                    key={item._id}
                                    projectId={projectId}
                                    onDelete={() => handleDeleteThisThen(item._id)}
                                    index={index}
                                    item={item}
                                    moveItem={moveThisThenItem}
                                    onSelect={handleThisThenSelect}
                                    isSelected={item._id === selectedThisThenId}
                                    projectData={projectData}
                                    BASE_URL={BASE_URL}
                                    token={token}
                                    setImage={setImage}
                                />
                            ))}
                        </Box>
                    </Paper>
                    
                    {/* Main Content Area */}
                    <Box sx={{ 
                        flex: 1, 
                        p: 2,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {currentThisThen ? (
                            <Grid container spacing={2} sx={{ height: '100%' }}>
                                {/* First Section */}
                                <Grid item xs={6} sx={{ height: '100%' }}>
                                    <Paper 
                                        elevation={2} 
                                        sx={{ 
                                            height: '100%', 
                                            display: 'flex', 
                                            flexDirection: 'column',
                                            overflow: 'hidden',
                                            borderRadius: 1
                                        }}
                                    >
                                        <Box sx={{ 
                                            bgcolor: 'var(--primary-color)', 
                                            color: 'white',
                                            px: 2,
                                            py: 1,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                                FIRST
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => openModal(true)}
                                                disabled={currentThisThen.this.images.length >= MAX_IMAGES_PER_SECTION}
                                                sx={{ 
                                                    bgcolor: 'white', 
                                                    color: 'var(--primary-color)',
                                                    '&:hover': {
                                                        bgcolor: 'var(--light-background)'
                                                    }
                                                }}
                                            >
                                                Add Image
                                            </Button>
                                        </Box>
                                        
                                        <Box sx={{ 
                                            p: 2,
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                            overflowY: 'auto',
                                            flex: 1
                                        }}>
                                            {currentThisThen.this.images.length === 0 ? (
                                                <Box sx={{ 
                                                    width: '100%', 
                                                    height: '100%', 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    color: 'text.secondary',
                                                    flexDirection: 'column',
                                                    gap: 1
                                                }}>
                                                    <Typography variant="body2">No images added yet</Typography>
                                                    <Button 
                                                        variant="outlined" 
                                                        size="small" 
                                                        startIcon={<AddIcon />}
                                                        onClick={() => openModal(true)}
                                                    >
                                                        Add First Image
                                                    </Button>
                                                </Box>
                                            ) : (
                                                currentThisThen.this.images.map((image, index) => (
                                                    image && (
                                                        <StyledImageItem key={index}>
                                                            <StyledImage
                                                                src={image.imageUrl}
                                                                alt={image.altName || image.name}
                                                            />
                                                            <TextField
                                                                variant="outlined"
                                                                size="small"
                                                                value={image.altName || image.name || ""}
                                                                onChange={(e) => handleImageNameChange(image.id, e.target.value, true)}
                                                                placeholder="Image name"
                                                                fullWidth
                                                                margin="dense"
                                                                sx={{ mt: 0.5 }}
                                                                inputProps={{ style: { padding: '6px 8px' } }}
                                                            />
                                                            <DeleteButton
                                                                className="delete-button"
                                                                size="small"
                                                                onClick={() => handleDeleteImage(index, true)}
                                                                aria-label="delete image"
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </DeleteButton>
                                                        </StyledImageItem>
                                                    )
                                                ))
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                                
                                {/* Then Section */}
                                <Grid item xs={6} sx={{ height: '100%' }}>
                                    <Paper 
                                        elevation={2} 
                                        sx={{ 
                                            height: '100%', 
                                            display: 'flex', 
                                            flexDirection: 'column',
                                            overflow: 'hidden',
                                            borderRadius: 1
                                        }}
                                    >
                                        <Box sx={{ 
                                            bgcolor: 'var(--secondary-color)', 
                                            color: 'white',
                                            px: 2,
                                            py: 1,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                                THEN
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => openModal(false)}
                                                disabled={currentThisThen.then.images.length >= MAX_IMAGES_PER_SECTION}
                                                sx={{ 
                                                    bgcolor: 'white', 
                                                    color: 'var(--secondary-color)',
                                                    '&:hover': {
                                                        bgcolor: 'var(--light-background)'
                                                    }
                                                }}
                                            >
                                                Add Image
                                            </Button>
                                        </Box>
                                        
                                        <Box sx={{ 
                                            p: 2,
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                            overflowY: 'auto',
                                            flex: 1
                                        }}>
                                            {currentThisThen.then.images.length === 0 ? (
                                                <Box sx={{ 
                                                    width: '100%', 
                                                    height: '100%', 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    color: 'text.secondary',
                                                    flexDirection: 'column',
                                                    gap: 1
                                                }}>
                                                    <Typography variant="body2">No images added yet</Typography>
                                                    <Button 
                                                        variant="outlined" 
                                                        size="small" 
                                                        startIcon={<AddIcon />}
                                                        onClick={() => openModal(false)}
                                                    >
                                                        Add Then Image
                                                    </Button>
                                                </Box>
                                            ) : (
                                                currentThisThen.then.images.map((image, index) => (
                                                    image && (
                                                        <StyledImageItem key={index}>
                                                            <StyledImage
                                                                src={image.imageUrl}
                                                                alt={image.altName || image.name}
                                                            />
                                                            <TextField
                                                                variant="outlined"
                                                                size="small"
                                                                value={image.altName || image.name || ""}
                                                                onChange={(e) => handleImageNameChange(image.id, e.target.value, false)}
                                                                placeholder="Image name"
                                                                fullWidth
                                                                margin="dense"
                                                                sx={{ mt: 0.5 }}
                                                                inputProps={{ style: { padding: '6px 8px' } }}
                                                            />
                                                            <DeleteButton
                                                                className="delete-button"
                                                                size="small"
                                                                onClick={() => handleDeleteImage(index, false)}
                                                                aria-label="delete image"
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </DeleteButton>
                                                        </StyledImageItem>
                                                    )
                                                ))
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        ) : (
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                height: '100%',
                                flexDirection: 'column',
                                gap: 2
                            }}>
                                <Typography variant="h6" color="text.secondary">
                                    Select a sequence from the sidebar or create a new one
                                </Typography>
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    startIcon={<AddIcon />}
                                    onClick={handleCreateNewThisThen}
                                >
                                    Create New Sequence
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
                
                <ImageSelectionModal
                    isOpen={isModalOpen}
                    onRequestClose={closeModal}
                    onSelectImage={handleSelectImage}
                    projectId={projectId}
                    token={token}
                    onClose={() => setIsModalOpen(false)}
                />
            </DndProvider>
            
            <BottomNavBar projectId={projectId} />
        </Box>
        </ThemeProvider>
    );
};

export default ThisThenEditor;
